import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import useApplicationInsights, {
  ApplicationInsightsConfig,
  ApplicationInsightsHook,
} from '@raas/shared/src/hooks/useApplicationInsights';

interface ApplicationInsightsContextType extends ApplicationInsightsHook {
  config: ApplicationInsightsConfig;
}

const ApplicationInsightsContext = createContext<ApplicationInsightsContextType | null>(null);

interface ApplicationInsightsProviderProps {
  children: ReactNode;
  config: ApplicationInsightsConfig;
}

export const ApplicationInsightsProvider: React.FC<ApplicationInsightsProviderProps> = ({
  children,
  config,
}) => {
  const applicationInsights = useApplicationInsights(config);

  const value: ApplicationInsightsContextType = useMemo(
    () => ({
      ...applicationInsights,
      config,
    }),
    [applicationInsights, config]
  );

  return (
    <ApplicationInsightsContext.Provider value={value}>
      {children}
    </ApplicationInsightsContext.Provider>
  );
};

export const useApplicationInsightsContext = (): ApplicationInsightsContextType => {
  const context = useContext(ApplicationInsightsContext);
  if (!context) {
    throw new Error(
      'useApplicationInsightsContext must be used within an ApplicationInsightsProvider'
    );
  }
  return context;
};

export default ApplicationInsightsContext;
